import { ago, COMMENT_MAX, type CommentDto, fmtDateTime, type PeopleMap } from '@hrms/shared'
import { useId, useState } from 'react'
import { LuEyeOff, LuNotebookPen, LuSend } from 'react-icons/lu'
import { Button, useToast } from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import { useAddComment } from './api.ts'
import { PersonAvatar, personName } from './ui.tsx'

export function CommentThread({
  comments,
  people,
  now,
  hrView,
}: {
  comments: CommentDto[]
  people: PeopleMap
  now: number
  hrView: boolean
}) {
  if (comments.length === 0)
    return (
      <p className="py-4 text-muted">
        No comments yet.
        {hrView
          ? ' Reply to the employee or leave an internal note for HR.'
          : ' Add any details HR should know.'}
      </p>
    )
  return (
    <ol className="flex flex-col gap-4">
      {comments.map((c) => {
        const author = people[c.authorId]
        const isHr = author?.role === 'admin'
        return (
          <li key={c.id} className="flex items-start gap-2.5">
            <PersonAvatar person={author} id={c.authorId} size="lg" />
            <div
              className={cn(
                'min-w-0 flex-1',
                c.internal && 'rounded-card border border-note-border bg-note-bg px-3 py-2',
              )}
            >
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="font-semibold">{personName(people, c.authorId)}</span>
                {isHr && !c.internal && (
                  <span
                    className="text-[11.5px] font-semibold text-accent"
                    {...hint('Reply from the HR team')}
                  >
                    HR
                  </span>
                )}
                {c.internal && (
                  <span
                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-amber-fg"
                    {...hint('Only the HR team can see internal notes')}
                  >
                    <LuEyeOff aria-hidden className="size-3" /> Internal note
                  </span>
                )}
                <time
                  dateTime={new Date(c.createdAt).toISOString()}
                  className="text-[12.5px] text-faint"
                  {...hint(fmtDateTime(c.createdAt, { now }))}
                >
                  {ago(c.createdAt, { now })}
                </time>
              </div>
              <p className="mt-0.5 break-words whitespace-pre-wrap">{c.body}</p>
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/** Unsent text and the Reply/Note mode per ticket, kept while the drawer re-renders or closes. */
const drafts = new Map<string, string>()
const modes = new Map<string, 'reply' | 'note'>()

/**
 * Comment box. Employees: "Add comment" (public). HR: a [Reply to employee | Internal note]
 * switch; note mode turns the box yellow (DESIGN.md §3.5 "Composer").
 */
export function Composer({
  ticketKey,
  hrView,
  canNote,
  raiserFirstName,
}: {
  ticketKey: string
  hrView: boolean
  /** Shows the Reply / Internal note switch (HR team). */
  canNote: boolean
  raiserFirstName: string
}) {
  const [text, setText] = useState(() => drafts.get(ticketKey) ?? '')
  const [mode, setModeState] = useState<'reply' | 'note'>(() =>
    canNote ? (modes.get(ticketKey) ?? 'reply') : 'reply',
  )
  const add = useAddComment(ticketKey)
  const toast = useToast()
  const id = useId()
  const note = canNote && mode === 'note'

  const setMode = (m: 'reply' | 'note') => {
    setModeState(m)
    modes.set(ticketKey, m)
  }
  const update = (v: string) => {
    setText(v)
    if (v) drafts.set(ticketKey, v)
    else drafts.delete(ticketKey)
  }

  const send = async () => {
    const body = text.trim()
    if (!body || add.isPending) return
    try {
      const res = await add.mutateAsync({ body, internal: note })
      update('')
      toast(
        note
          ? 'Internal note added'
          : res.resumed
            ? 'Reply sent. The ticket is back with HR.'
            : hrView
              ? 'Reply sent'
              : 'Comment added',
      )
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Couldn’t save that change. Try again.')
    }
  }

  const placeholder = note
    ? 'Write a note only HR can see…'
    : hrView
      ? `Reply to ${raiserFirstName}…`
      : 'Add a comment…'

  return (
    <div
      className={cn(
        'mt-5 rounded-panel border transition-colors',
        note
          ? 'border-note-border bg-note-bg focus-within:border-amber-fg'
          : 'border-border-strong bg-surface focus-within:border-accent',
      )}
    >
      <label htmlFor={id} className="sr-only">
        {note ? 'Internal note' : hrView ? 'Reply to the employee' : 'Add a comment'}
      </label>
      <textarea
        id={id}
        value={text}
        maxLength={COMMENT_MAX}
        onChange={(e) => update(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault()
            void send()
          }
        }}
        placeholder={placeholder}
        className="block min-h-[84px] w-full resize-y rounded-t-panel bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-faint"
      />
      <div
        className={cn(
          'flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2',
          note ? 'border-note-border' : 'border-border',
        )}
      >
        {canNote ? (
          <fieldset
            data-tour="composer-mode"
            className="m-0 inline-flex min-w-0 rounded-ctl border border-border bg-surface-2 p-0.5"
          >
            <legend className="sr-only">Comment type</legend>
            {(
              [
                ['reply', 'Reply to employee', `${raiserFirstName} will see this reply`],
                ['note', 'Internal note', 'Only the HR team can see internal notes'],
              ] as const
            ).map(([m, label, tip]) => (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                onClick={() => setMode(m)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-seg px-2.5 py-[3px] text-[13px] font-medium transition-colors',
                  mode === m ? 'bg-surface text-text shadow-seg' : 'text-muted hover:text-text',
                )}
                {...hint(tip)}
              >
                {m === 'note' && <LuEyeOff aria-hidden className="size-3.5 text-amber-fg" />}
                {label}
              </button>
            ))}
          </fieldset>
        ) : (
          <span className="text-[12.5px] text-muted">
            {hrView ? `${raiserFirstName} will see this reply.` : 'HR will see this on the ticket.'}
          </span>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={() => void send()}
          disabled={!text.trim() || add.isPending}
          {...hint('Ctrl or Cmd + Enter')}
        >
          {note ? (
            <LuNotebookPen aria-hidden className="size-3.5" />
          ) : (
            <LuSend aria-hidden className="size-3.5" />
          )}
          {note ? 'Add note' : hrView ? 'Send reply' : 'Add comment'}
        </Button>
      </div>
    </div>
  )
}
